/**
 * Turning notifications on, from the phone's side.
 *
 * Three facts shape this and all three are Safari's:
 *
 *   iOS delivers Web Push ONLY to a PWA that has been added to the home screen.
 *   Not "works worse in the browser" — the API is absent entirely, so the check
 *   has to be for installation rather than for support, or every iPhone gets
 *   told its browser is too old.
 *
 *   The permission prompt must come from a real tap. Asking on load is both
 *   refused and wasteful: a denial is permanent until the app is deleted and
 *   reinstalled, and there is no way to ask again.
 *
 *   The subscription belongs to the device, not the account. Two people sharing
 *   a phone would share notifications, which for this group is a feature and
 *   not worth engineering around.
 */

import { call } from './store'

export type PushState =
  /** No Web Push here at all — usually iOS Safari outside the home screen. */
  | 'unavailable'
  /** Supported, but iOS needs the app installed first. */
  | 'needs-install'
  /** Available and not yet asked. */
  | 'off'
  /** Subscribed on this device. */
  | 'on'
  /** Refused. Only recoverable in system settings, so say so plainly. */
  | 'blocked'

export const isStandalone = (): boolean =>
  window.matchMedia?.('(display-mode: standalone)').matches ||
  // Safari's own flag, which predates the standard media query and is the only
  // signal available on an iPhone.
  (navigator as unknown as { standalone?: boolean }).standalone === true

export const isApple = (): boolean =>
  /iP(hone|ad|od)/.test(navigator.userAgent) ||
  // iPadOS reports itself as a Mac; the touch points give it away.
  (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)

export async function pushState(): Promise<PushState> {
  const supported =
    'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window
  if (!supported) return isApple() && !isStandalone() ? 'needs-install' : 'unavailable'
  if (Notification.permission === 'denied') return 'blocked'

  const reg = await navigator.serviceWorker.ready
  const sub = await reg.pushManager.getSubscription()
  if (!sub) return 'off'

  /*
   * The browser saying yes is only half an answer.
   *
   * This used to return 'on' the moment the browser held a subscription, and
   * the browser holds one long after the server has thrown the row away —
   * Apple invalidates tokens, and `flushDue` deletes on a 404 because keeping
   * a dead endpoint costs a failed request per birdie for the week. The result
   * was a screen that said "registered" forever while nothing could reach the
   * phone, which is exactly the failure you cannot debug from the sofa.
   *
   * So the browser's subscription is re-posted instead of trusted. The write is
   * an upsert keyed on the endpoint, so it is free when the server already
   * agrees and self-healing when it does not. Failure leaves it 'on': the
   * subscription is real, this call is a reconciliation, and being offline is
   * not a reason to tell somebody their notifications are off.
   */
  await call('push/subscribe', { method: 'POST', body: JSON.stringify(sub.toJSON()) }).catch(
    () => undefined,
  )
  return 'on'
}

/** The server's public VAPID key, as the raw bytes `subscribe` wants. */
function decodeKey(base64url: string): Uint8Array<ArrayBuffer> {
  const pad = base64url.replace(/-/g, '+').replace(/_/g, '/')
  const raw = atob(pad + '='.repeat((4 - (pad.length % 4)) % 4))
  const out = new Uint8Array(new ArrayBuffer(raw.length))
  for (let i = 0; i < raw.length; i += 1) out[i] = raw.charCodeAt(i)
  return out
}

/**
 * Ask, subscribe, and register with the server. Must be called from a tap.
 *
 * Returns the resulting state rather than throwing on refusal — a denied
 * permission is an answer, not an error, and the screen needs to say something
 * different for it than for a failure.
 */
export async function enablePush(): Promise<PushState> {
  const permission = await Notification.requestPermission()
  if (permission !== 'granted') return permission === 'denied' ? 'blocked' : 'off'

  const { key } = await call<{ key: string | null }>('push/key')
  if (!key) throw new Error('Notifications are not configured on the server yet.')

  const reg = await navigator.serviceWorker.ready
  // Reuse an existing subscription: re-subscribing returns the same endpoint
  // anyway, and asking for a new one while the old is live throws.
  const sub =
    (await reg.pushManager.getSubscription()) ??
    (await reg.pushManager.subscribe({
      // Required by every browser: a push that shows nothing is not allowed.
      userVisibleOnly: true,
      applicationServerKey: decodeKey(key),
    }))

  await call('push/subscribe', { method: 'POST', body: JSON.stringify(sub.toJSON()) })
  return 'on'
}

/** Unsubscribe here and forget the device on the server. */
export async function disablePush(): Promise<PushState> {
  const reg = await navigator.serviceWorker.ready
  const sub = await reg.pushManager.getSubscription()
  if (sub) {
    // Server first: if the local unsubscribe succeeds and the POST does not,
    // the server keeps pushing to an endpoint nothing is listening on.
    await call('push/unsubscribe', {
      method: 'POST',
      body: JSON.stringify({ endpoint: sub.endpoint }),
    }).catch(() => undefined)
    await sub.unsubscribe()
  }
  return 'off'
}
